// T015 & T040: Deck Store with DeckValidator Integration

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CardDefinition, Faction } from '../types';
import { DeckValidator, type DeckValidationResult, type CanAddResult } from '../core/rules/DeckValidator';
import { CardLoader } from '../core/data/CardLoader';
import {
  listDecks,
  createDeck,
  deleteDeck as apiDeleteDeck,
  type DeckInfo,
} from '../services/NetworkManager';
import { useAuthStore } from './authStore';

interface SavedDeck {
  id: string;
  name: string;
  faction: Faction;
  cardIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

interface DeckStore {
  // State
  availableCards: CardDefinition[];
  savedDecks: SavedDeck[];
  currentDeckId: string | null;
  currentDeckCards: CardDefinition[];
  selectedFaction: Faction | null;
  isInitialized: boolean;

  // Server sync state
  serverDecks: DeckInfo[];
  isLoadingDecks: boolean;
  selectedDeckId: string | null;

  // Actions
  initialize: () => void;
  selectFaction: (faction: Faction) => void;

  // Deck building
  addCardToDeck: (cardId: string) => CanAddResult;
  removeCardFromDeck: (cardId: string) => void;
  clearDeck: () => void;

  // Validation
  validateDeck: () => DeckValidationResult;
  canAddCard: (cardId: string) => CanAddResult;

  // Persistence (local)
  saveDeck: (name: string) => string;
  loadDeck: (deckId: string) => void;
  deleteDeck: (deckId: string) => void;

  // Server sync actions
  fetchDecks: () => Promise<void>;
  saveDeckToServer: (name: string) => Promise<string>;
  deleteDeckFromServer: (deckId: string) => Promise<void>;
  selectDeckForBattle: (deckId: string) => void;

  // Helpers
  getCurrentDeckCardIds: () => string[];
  getDeckStats: () => { total: number; factionCards: number; neutralCards: number; mana: number };
}

export const useDeckStore = create<DeckStore>()(
  devtools(
    persist(
      (set, get) => ({
        availableCards: [],
        savedDecks: [],
        currentDeckId: null,
        currentDeckCards: [],
        selectedFaction: null,
        isInitialized: false,
        serverDecks: [],
        isLoadingDecks: false,
        selectedDeckId: null,

        initialize: () => {
          const { isInitialized } = get();
          if (isInitialized) return;

          const allCards = CardLoader.getAllCards();
          set({
            availableCards: allCards,
            isInitialized: true,
          });
        },

        selectFaction: (faction) => {
          const allCards = CardLoader.getAllCards();
          const availableForFaction = CardLoader.getAvailableCardsForDeck(faction);

          set({
            selectedFaction: faction,
            currentDeckCards: [],
            currentDeckId: null,
            availableCards: availableForFaction.length > 0 ? availableForFaction : allCards,
          });
        },

        addCardToDeck: (cardId) => {
          const { currentDeckCards, selectedFaction } = get();

          if (!selectedFaction) {
            return { canAdd: false, reason: 'WRONG_FACTION' as const };
          }

          const card = CardLoader.getCardById(cardId);
          if (!card) {
            return { canAdd: false, reason: 'WRONG_FACTION' as const };
          }

          const result = DeckValidator.canAddCard(currentDeckCards, card, selectedFaction);

          if (result.canAdd) {
            set({ currentDeckCards: [...currentDeckCards, card] });
          }

          return result;
        },

        removeCardFromDeck: (cardId) => {
          const { currentDeckCards } = get();
          const index = currentDeckCards.findIndex((c) => c.id === cardId);
          if (index === -1) return;

          const newCards = [...currentDeckCards];
          newCards.splice(index, 1);
          set({ currentDeckCards: newCards });
        },

        clearDeck: () => {
          set({ currentDeckCards: [], currentDeckId: null });
        },

        validateDeck: () => {
          const { currentDeckCards, selectedFaction } = get();

          if (!selectedFaction) {
            return {
              isValid: false,
              errors: ['NO_FACTION_CARDS' as const],
            };
          }

          return DeckValidator.validate(currentDeckCards, selectedFaction);
        },

        canAddCard: (cardId) => {
          const { currentDeckCards, selectedFaction } = get();

          if (!selectedFaction) {
            return { canAdd: false, reason: 'WRONG_FACTION' as const };
          }

          const card = CardLoader.getCardById(cardId);
          if (!card) {
            return { canAdd: false, reason: 'WRONG_FACTION' as const };
          }

          return DeckValidator.canAddCard(currentDeckCards, card, selectedFaction);
        },

        saveDeck: (name) => {
          const { currentDeckCards, selectedFaction, savedDecks, validateDeck } = get();

          if (!selectedFaction) return '';

          // Validate before saving
          const validation = validateDeck();
          if (!validation.isValid) {
            return '';
          }

          const deckId = `deck_${String(Date.now())}`;
          const newDeck: SavedDeck = {
            id: deckId,
            name,
            faction: selectedFaction,
            cardIds: currentDeckCards.map((c) => c.id),
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          set({
            savedDecks: [...savedDecks, newDeck],
            currentDeckId: deckId,
          });

          return deckId;
        },

        loadDeck: (deckId) => {
          const { savedDecks } = get();
          const deck = savedDecks.find((d) => d.id === deckId);

          if (deck) {
            const cards = deck.cardIds
              .map((id) => CardLoader.getCardById(id))
              .filter((c): c is CardDefinition => c !== undefined);

            set({
              currentDeckId: deckId,
              currentDeckCards: cards,
              selectedFaction: deck.faction,
            });
          }
        },

        deleteDeck: (deckId) => {
          const { savedDecks, currentDeckId } = get();

          set({
            savedDecks: savedDecks.filter((d) => d.id !== deckId),
            currentDeckId: currentDeckId === deckId ? null : currentDeckId,
          });
        },

        getCurrentDeckCardIds: () => {
          const { currentDeckCards } = get();
          return currentDeckCards.map((c) => c.id);
        },

        getDeckStats: () => {
          const { currentDeckCards, selectedFaction } = get();

          let factionCards = 0;
          let neutralCards = 0;
          let mana = 0;

          for (const card of currentDeckCards) {
            if (card.faction === selectedFaction) {
              factionCards++;
            } else if (card.faction === 'neutral_spell' || card.faction === 'neutral_unit') {
              neutralCards++;
            }

            if ((card.type === 'spell' || card.type === 'field') && card.cost) {
              mana += card.cost;
            }
          }

          return {
            total: currentDeckCards.length,
            factionCards,
            neutralCards,
            mana,
          };
        },

        fetchDecks: async () => {
          const authState = useAuthStore.getState();
          if (!authState.isLoggedIn || !authState.token) return;

          set({ isLoadingDecks: true });
          try {
            const decks = await listDecks(authState.token);
            set({ serverDecks: decks, isLoadingDecks: false });
            // Auto-select first deck if none selected
            if (!get().selectedDeckId && decks.length > 0) {
              set({ selectedDeckId: decks[0].id });
            }
          } catch {
            set({ isLoadingDecks: false });
          }
        },

        saveDeckToServer: async (name) => {
          const authState = useAuthStore.getState();
          if (!authState.isLoggedIn || !authState.token) return '';

          const { currentDeckCards, selectedFaction } = get();
          if (!selectedFaction) return '';

          try {
            const deck = await createDeck(authState.token, {
              name,
              faction: selectedFaction,
              card_ids: currentDeckCards.map((c) => c.id),
            });
            await get().fetchDecks();
            return deck.id;
          } catch {
            return '';
          }
        },

        deleteDeckFromServer: async (deckId) => {
          const authState = useAuthStore.getState();
          if (!authState.isLoggedIn || !authState.token) return;

          try {
            await apiDeleteDeck(authState.token, deckId);
            const { selectedDeckId } = get();
            if (selectedDeckId === deckId) {
              set({ selectedDeckId: null });
            }
            await get().fetchDecks();
          } catch {
            // ignore
          }
        },

        selectDeckForBattle: (deckId) => {
          set({ selectedDeckId: deckId });
        },
      }),
      {
        name: 'xinglingwang-decks',
        partialize: (state) => ({
          savedDecks: state.savedDecks,
        }),
      }
    ),
    { name: 'DeckStore' }
  )
);
